import { api } from 'services/api/baseBackendAPI';

export const fundInvestmentsApi = api.injectEndpoints({
  endpoints: (build) => ({
    saveFundInvestmentFund: build.mutation({
      query: (data) => ({
        url: '/funds-investment/saveFund',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['FundInvestments', 'Funds'],
    }),

    saveFundInvestmentAsset: build.mutation({
      query: (data) => ({
        url: '/funds-investment/saveAsset',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['FundInvestments', 'Assets'],
    }),

    saveFundInvestmentCompany: build.mutation({
      query: (data) => ({
        url: '/funds-investment/saveCompany',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['FundInvestments', 'Companies'],
    }),

    // Global Categorized Query
    getAllFundInvestmentsCategorized: build.query({
      query: (fund_id) => ({
        url: `funds-investment/getFundInvestmentsCategorized`,
        method: 'GET',
        params: { fund_id },
      }),
      transformResponse: (response) => response.data,
      providesTags: (result) => {
        if (result?.data) {
          // Collect IDs from all three categories for granular cache invalidation
          const fundTags = result.data.funds.map(({ investment_asset_id }) => ({
            type: 'Fund Investment',
            id: investment_asset_id,
          }));
          const companyTags = result.data.companies.map(
            ({ investment_asset_id }) => ({
              type: 'Fund Investment',
              id: investment_asset_id,
            })
          );
          const assetTags = result.data.assets.map(
            ({ investment_asset_id }) => ({
              type: 'Fund Investment',
              id: investment_asset_id,
            })
          );

          return [
            ...fundTags,
            ...companyTags,
            ...assetTags,
            { type: 'Fund Investment', id: 'LIST' },
          ];
        }
        return [{ type: 'Fund Investment', id: 'LIST' }];
      },
    }),

    // Existing individual fund portfolio query
    getAllFundInvestments: build.query({
      query: (fund_id) => ({
        url: `funds/get-fund-portfolio-company`,
        method: 'GET',
        params: { fund_id }, // Changed from body to params for GET request
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ investment_asset_id }) => ({
                type: 'Fund Investment',
                id: investment_asset_id,
              })),
              { type: 'Fund Investment', id: 'LIST' },
            ]
          : [{ type: 'Fund Investment', id: 'LIST' }],
    }),

    createFundInvestmentFund: build.mutation({
      query: (data) => ({
        url: `funds-investment/createFund`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Fund Investment', 'Fund'],
    }),

    createFundInvestmentCompany: build.mutation({
      query: (data) => ({
        url: `funds-investment/createCompany`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Fund Investment', 'Company'],
    }),

    createFundInvestmentAsset: build.mutation({
      query: (data) => ({
        url: `funds-investment/createAsset`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Fund Investment', 'Asset'],
    }),

    editFundInvestment: build.mutation({
      query: (data) => ({
        url: `funds-investment/editInvestment`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error, { investment_asset_id }) => [
        { type: 'Fund Investment', id: investment_asset_id },
        { type: 'Fund Investment', id: 'LIST' },
      ],
    }),

    deleteFundInvestment: build.mutation({
      query: (data) => ({
        url: `funds-investment/deleteInvestment`,
        method: 'DELETE',
        params: data,
      }),
      invalidatesTags: [{ type: 'Fund Investment', id: 'LIST' }],
    }),
  }),
  overrideExisting: true,
});

export const {
  useCreateFundInvestmentAssetMutation,
  useCreateFundInvestmentCompanyMutation,
  useCreateFundInvestmentFundMutation,
  useGetAllFundInvestmentsQuery,
  useGetAllFundInvestmentsCategorizedQuery,
  useEditFundInvestmentMutation,
  useDeleteFundInvestmentMutation,
  useSaveFundInvestmentAssetMutation,
  useSaveFundInvestmentCompanyMutation,
  useSaveFundInvestmentFundMutation,
} = fundInvestmentsApi;
